import { type FormEvent, useEffect, useState } from "react";
import {
  confirmSignIn,
  getCurrentUser,
  signIn,
  type SignInOutput,
} from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import edgeCareLogo from "@/assets/edge-care-logo.png";

type Challenge = "credentials" | "new-password" | "mfa-code" | "totp-setup";

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [challenge, setChallenge] = useState<Challenge>("credentials");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      try {
        await getCurrentUser();
        navigate("/", { replace: true });
      } catch {
        // No current session; keep the sign-in form visible.
      }
    };

    void checkUser();
  }, [navigate]);

  const processNextStep = (result: SignInOutput) => {
    const step = result.nextStep.signInStep;

    if (step === "DONE") {
      navigate("/");
      return;
    }

    if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setChallenge("new-password");
      return;
    }

    if (step === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
      setTotpSecret(result.nextStep.totpSetupDetails.sharedSecret);
      setChallenge("totp-setup");
      return;
    }

    if (
      step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
      step === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
    ) {
      setChallenge("mfa-code");
      return;
    }

    throw new Error(`Unsupported sign-in step: ${step}`);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (challenge === "new-password" && newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      if (challenge === "credentials") {
        processNextStep(await signIn({ username, password }));
      } else if (challenge === "new-password") {
        processNextStep(await confirmSignIn({ challengeResponse: newPassword }));
      } else {
        processNextStep(await confirmSignIn({ challengeResponse: code }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const challengeTitle =
    challenge === "new-password"
      ? "Set new password"
      : challenge === "totp-setup"
        ? "Set up authenticator"
        : challenge === "mfa-code"
          ? "Enter verification code"
          : "Login";

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-8">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-96 rounded-lg bg-white p-8 shadow-md"
      >
        <img
          src={edgeCareLogo}
          alt="EdgeCare — Leading-edge Research for Better Care"
          className="mx-auto mb-3 size-40 object-contain"
        />

        <h1 className="mb-6 text-center text-2xl font-semibold">
          {challengeTitle}
        </h1>

        {challenge === "credentials" && (
          <>
            <div className="mb-4">
              <label className="mb-1 block text-sm">Username</label>
              <input
                type="text"
                className="w-full rounded-lg border px-3 py-2"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
            </div>

            <div className="mb-4">
              <label className="mb-1 block text-sm">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full rounded-lg border px-3 py-2 pr-10"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </>
        )}

        {challenge === "new-password" && (
          <>
            <div className="mb-4">
              <label className="mb-1 block text-sm">New password</label>
              <input
                type="password"
                className="w-full rounded-lg border px-3 py-2"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
              />
            </div>
            <div className="mb-4">
              <label className="mb-1 block text-sm">Confirm password</label>
              <input
                type="password"
                className="w-full rounded-lg border px-3 py-2"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
            </div>
          </>
        )}

        {(challenge === "mfa-code" || challenge === "totp-setup") && (
          <>
            {challenge === "totp-setup" && (
              <div className="mb-4">
                <label className="mb-1 block text-sm">Authenticator setup key</label>
                <input
                  type="text"
                  className="w-full rounded-lg border bg-gray-50 px-3 py-2 font-mono text-sm"
                  value={totpSecret}
                  readOnly
                />
              </div>
            )}
            <div className="mb-4">
              <label className="mb-1 block text-sm">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="w-full rounded-lg border px-3 py-2"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
              />
            </div>
          </>
        )}

        {error && <div className="mb-3 text-sm text-red-500">{error}</div>}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg bg-blue-600 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? "Working..." : challenge === "credentials" ? "Login" : "Continue"}
        </button>
      </form>
    </div>
  );
}
